import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  ErrorWidget, dateHelper, useI18n, useRootContext,
} from '@jarvis/react-portal-addons';
import { useSummaryOfAllJobsApiCall } from '../../../hooks/useSummaryOfAllJobsApiCall';
import { Table } from './Table';
import {
  COLOR_MODES,
  COLUMNS, TAB_CONFIG, VIEW_TYPES,
} from './utils';
import { StatusColumn } from './StatusColumn';
import { ColorColumn } from './ColorColumn';
import {
  AddonsWidgetContainer,
  Button,
  Card,
  CardContainer,
  ContentContainer,
  ControlButtonsContainer,
  Description,
  EmptyStateMessage,
  HeaderContainer,
  HeaderText,
  IconWarning,
  LoaderWidget,
  NoDataAvailableMessage,
  WarningIconContainer,
} from './styles';

const PrivatePickupWidget = ({ defaultView, 'data-testid': dataTestId, deviceCache }) => {
  const [activeView, setActiveView] = useState(defaultView);
  const { t } = useI18n();
  const { shell: { localization } } = useRootContext();

  const {
    formatDateNumerical,
    formatTime,
  } = dateHelper;

  const summaryOfAllJobs = useSummaryOfAllJobsApiCall({ init: false });

  // Fetch data when switching to Print History tab if we haven't fetched it yet
  useEffect(() => {
    if (
      activeView === VIEW_TYPES.RECENT_PRINT_HISTORY
      && !summaryOfAllJobs.data
      && !summaryOfAllJobs.pending
      && !summaryOfAllJobs.error) {
      summaryOfAllJobs.forceFetch();
    }
  }, [activeView, deviceCache, summaryOfAllJobs]);

  // Only call the APIs that failed
  const handleRetry = useCallback(() => {
    if (deviceCache.error) {
      deviceCache.forceFetch();
    }

    if (summaryOfAllJobs.error) {
      summaryOfAllJobs.forceFetch();
    }
  }, [deviceCache, summaryOfAllJobs]);

  const handleViewChange = useCallback(newView => {
    setActiveView(newView);
  }, []);

  const tabsWithTranslations = useMemo(() => TAB_CONFIG.map(tab => ({
    ...tab,
    label: t(tab.labelKey),
  })), [t]);

  const getPrinterNameById = useCallback(id => {
    const device = deviceCache?.data?.find(d => d.deviceId === id);
    return device?.identity?.makeAndModel?.name || '';
  }, [deviceCache]);

  const getFormattedTime = useCallback(time => {
    if (!time) {
      return null;
    }

    const date = new Date(time);
    const formattedDate = formatDateNumerical(date, localization.locale);
    const formattedTime = formatTime(date, localization.locale);

    return t('dashboard.privatePickupFiles.timestamp', {
      date: formattedDate,
      time: formattedTime,
      interpolation: { escapeValue: false },
    });
  }, [t, formatDateNumerical, formatTime, localization.locale]);

  const formatApiData = useCallback(data => {
    const tableList = data?.printJobs?.flatMap(printerJobInfo => {
      const jobs = printerJobInfo.jobs?.map(job => ({
        ...job,
        deviceId: printerJobInfo.deviceId,
      })) || [];

      return jobs;
    }) || [];

    return tableList?.map((item, index) => {
      const id = index + 1;

      const useTricolorColor = item.colorMode === COLOR_MODES.COLOR || item.colorMode === COLOR_MODES.AUTO;

      return {
        fileName: <span data-testid={`private-pickup-files-filename-row-${id}`}>{item.jobName}</span>,
        status: <StatusColumn data-testid={`private-pickup-files-status-row-${id}`} type={item.status} />,
        printer: <span data-testid={`private-pickup-files-printer-row-${id}`}>{getPrinterNameById(item.deviceId)}</span>,
        pages: <span data-testid={`private-pickup-files-pages-row-${id}`}>{item.numPages}</span>,
        color: <ColorColumn data-testid={`private-pickup-files-color-row-${id}`} tricolor={useTricolorColor} />,
        submitTime: <span data-testid={`private-pickup-files-submittime-row-${id}`}>{getFormattedTime(item.jobCompletionTime)}</span>,
      };
    });
  }, [getFormattedTime, getPrinterNameById]);

  // Prepare table data based on active view
  const tableData = useMemo(() => {
    const formattedColumns = COLUMNS.map(col => ({
      id: col.id,
      label: t(col.labelKey),
    }));

    const preferences = {
      width: COLUMNS.map(col => ({
        columnId: col.id,
        maxWidth: col.width,
        minWidth: col.width,
      })),
    };

    switch (activeView) {
      case VIEW_TYPES.PICKUP_JOBS:
        return {
          columns: formattedColumns,
          preferences,
          data: [],
          source: VIEW_TYPES.PICKUP_JOBS,
          pending: false,
          error: false,
          description: t('dashboard.privatePickupFiles.privatePickupJobs.description'),
          emptyStateMessage: t('dashboard.privatePickupFiles.privatePickupJobs.emptyStateMessage'),
        };
      case VIEW_TYPES.RECENT_PRINT_HISTORY:
        return {
          columns: formattedColumns,
          preferences,
          data: formatApiData(summaryOfAllJobs?.data),
          source: VIEW_TYPES.RECENT_PRINT_HISTORY,
          pending: summaryOfAllJobs.pending || deviceCache.pending,
          error: summaryOfAllJobs.error || deviceCache.error,
          description: t('dashboard.privatePickupFiles.recentPrintHistory.description'),
          emptyStateMessage: t('dashboard.privatePickupFiles.recentPrintHistory.emptyStateMessage'),
        };
      default:
        return null;
    }
  }, [activeView, deviceCache, formatApiData, summaryOfAllJobs, t]);

  const tableIsEmpty = tableData.data?.length === 0;

  return (
    <Card
      data-testid={dataTestId}
      border="dropShadow"
      content={(
        <CardContainer>
          <HeaderContainer>
            <HeaderText
              className="subtitle-large"
              tagReplaceStart="<span class='caption-regular'>"
              tagReplaceEnd="</span>"
              data-testid="private-pickup-files-header"
            >
              {t('dashboard.privatePickupFiles.header')}
            </HeaderText>
          </HeaderContainer>

          <ContentContainer>
            <ControlButtonsContainer data-testid="view-toggle-buttons">
              {tabsWithTranslations.map(tab => (
                <Button
                  key={tab.id}
                  expanded
                  small
                  appearance={activeView === tab.id ? 'primary' : 'ghost'}
                  onClick={() => handleViewChange(tab.id)}
                  isSelected={activeView === tab.id}
                  data-testid={tab.testId}
                >
                  {tab.label}
                </Button>
              ))}
            </ControlButtonsContainer>

            <Description
              className="body-regular"
              data-testid="private-pickup-files-description"
            >
              {tableData.description}
            </Description>

            {tableData.pending && (
              <AddonsWidgetContainer data-testid="private-pickup-files-widget-loader">
                <LoaderWidget
                  messageClassName="caption-regular"
                  i18n={{ subTitle: t('dashboard.privatePickupFiles.loadingData') }}
                />
              </AddonsWidgetContainer>
            )}

            {!tableData.pending && tableData.error && (
              <AddonsWidgetContainer data-testid="private-pickup-files-widget-error">
                <ErrorWidget
                  onRetry={handleRetry}
                  i18n={{
                    retry: t('dashboard.error.retry'),
                    message: t('dashboard.error.bodyCopy'),
                  }}
                />
              </AddonsWidgetContainer>
            )}

            {!tableData.pending && !tableData.error && tableIsEmpty && (
              <AddonsWidgetContainer data-testid="private-pickup-files-widget-no-data">
                <WarningIconContainer>
                  <IconWarning size={24} />
                </WarningIconContainer>
                <NoDataAvailableMessage className="label-regular">
                  {t('dashboard.privatePickupFiles.noDataAvailable')}
                </NoDataAvailableMessage>
                <EmptyStateMessage className="caption-regular">{tableData.emptyStateMessage}</EmptyStateMessage>
              </AddonsWidgetContainer>
            )}

            {!tableData.pending && !tableData.error && !tableIsEmpty && (
              <Table {...tableData} />
            )}
          </ContentContainer>
        </CardContainer>
      )}
    />
  );
};

PrivatePickupWidget.propTypes = {
  defaultView: PropTypes.oneOf(Object.values(VIEW_TYPES)),
  'data-testid': PropTypes.string,
  deviceCache: PropTypes.shape({
    data: PropTypes.arrayOf(PropTypes.shape({
      deviceId: PropTypes.string.isRequired,
    })),
    error: PropTypes.bool,
    pending: PropTypes.bool,
    success: PropTypes.bool,
    forceFetch: PropTypes.func,
  }).isRequired,
};

PrivatePickupWidget.defaultProps = {
  defaultView: VIEW_TYPES.PICKUP_JOBS,
  'data-testid': 'private-pickup-files-widget',
};

export default PrivatePickupWidget;
